// TODO: replace it with something lighter
import React, { ChangeEvent } from 'react';
import { LinearProgress, styled, TextField, Typography } from '@mui/material';
import { StandardTextFieldProps } from '@mui/material/TextField';
import { Omit } from 'lib/types';
import logger from './logger';

const PREFIX = 'PasswordWithStrengthField';

const classes = {
    colorPrimary: `${PREFIX}-colorPrimary`,
    barColorPrimaryVeryWeak: `${PREFIX}-barColorPrimaryVeryWeak`,
    barColorPrimaryWeak: `${PREFIX}-barColorPrimaryWeak`,
    barColorPrimaryOkay: `${PREFIX}-barColorPrimaryOkay`,
    barColorPrimaryStrong: `${PREFIX}-barColorPrimaryStrong`,
    barColorPrimaryVeryStrong: `${PREFIX}-barColorPrimaryVeryStrong`,
    passwordInput: `${PREFIX}-passwordInput`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(() => ({
    [`& .${classes.colorPrimary}`]: {
        backgroundColor: '#B2DFDB',
        height: '10px'
    },

    [`& .${classes.barColorPrimaryVeryWeak}`]: {
        backgroundColor: '#d50000'
    },

    [`& .${classes.barColorPrimaryWeak}`]: {
        backgroundColor: '#c62828'
    },

    [`& .${classes.barColorPrimaryOkay}`]: {
        backgroundColor: '#ff9800'
    },

    [`& .${classes.barColorPrimaryStrong}`]: {
        backgroundColor: '#cddc39'
    },

    [`& .${classes.barColorPrimaryVeryStrong}`]: {
        backgroundColor: '#8bc34a'
    },

    [`& .${classes.passwordInput}`]: {
        marginBottom: '5px',
        width: '100%'
    }
}));

export interface PasswordWithStrengthFieldProps extends Omit<StandardTextFieldProps, 'classes' | 'onChange'> {
    onChange: (password: string) => void;
}

const scoreToStrengthTextAndClass = (score: number): string[] => {
    switch (score) {
        case 4:
            return ['Very Strong', classes.barColorPrimaryVeryStrong];
        case 3:
            return ['Strong', classes.barColorPrimaryStrong];
        case 2:
            return ['Okay', classes.barColorPrimaryOkay];
        case 1:
            return ['Weak', classes.barColorPrimaryWeak];
        default:
            return ['Very Weak', classes.barColorPrimaryVeryWeak];
    }
};

export const PasswordWithStrengthField: React.FC<PasswordWithStrengthFieldProps> = ({
    disabled,
    onChange,
    ...rest
}) => {
    const [passwordStrength, setPasswordStrength] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [zxcvbn, setZxcvbn] = React.useState<((value: string) => { score: number }) | undefined>(undefined);
    const [loadingError, setLoadingError] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        import('zxcvbn')
            .then(result => {
                setZxcvbn(() => result.default);
            })
            .catch(e => {
                logger.error(e.message, e);
                setLoadingError(e.message);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const onPasswordInputChanged = React.useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const password = event.target.value;
            if (zxcvbn) {
                const result = zxcvbn(password);
                setPasswordStrength(result.score);
            }
            onChange(password);
        },
        [zxcvbn, onChange]
    );

    const [strengthText, barColorPrimary] = scoreToStrengthTextAndClass(passwordStrength);

    return (
        <Root>
            <TextField
                disabled={disabled || loading || !!loadingError}
                {...rest}
                type="password"
                onChange={onPasswordInputChanged}
                className={classes.passwordInput}
            />
            <LinearProgress
                variant="determinate"
                value={20 * (passwordStrength + 1)}
                classes={{ barColorPrimary, colorPrimary: classes.colorPrimary }}
            />
            <Typography component="div" variant="caption" align="left">
                {strengthText} Password
            </Typography>
        </Root>
    );
};
